// src/client/renderer/vfx/env/fireworks-pool.ts
//
// Plan 18, Task 5.3 — Game over fireworks finale pool.
//
// Architecture choice (documented here as required by spec):
//   Per-particle Mesh approach chosen over THREE.Points.
//   Rationale: the test asserts `group.children.length` increases over time as
//   bursts fire, implying particles are added as children of a parent Group.
//   Per-Mesh ownership also simplifies individual particle disposal — each
//   particle owns its own SphereGeometry + MeshBasicMaterial clone so release()
//   and dispose() can walk the group and call geo.dispose() + mat.dispose() per
//   particle without shared-resource hazards.
//
// Physics model (per-particle, update-loop driven — NO gsap for individual particles):
//   - At burst time, spawn 60-90 particles at a random sky position.
//   - Each particle gets a random outward direction (spherical LCG sample) + speed [5,8].
//   - Per frame: vy += GRAVITY * dt; pos += v * dt; opacity = 1 - (age / TTL).
//   - At age > TTL, the particle is removed from the group and disposed.
//
// Burst schedule (deterministic):
//   8 bursts at: 0.0, 0.8, 1.5, 2.2, 3.0, 3.8, 4.5, 5.2 seconds.
//
// isFinished when BOTH:
//   (a) elapsed >= SCHEDULE_END (5.2s — last burst trigger), AND
//   (b) all spawned particles have aged past TTL (~1.8s).
//   Total emitter lifetime ≈ 5.2 + 1.8 = 7.0s minimum (spec says ~7.8s, so we
//   use DONE_THRESHOLD = 7.0s as the completion gate — update(dt=8.0) triggers it).
//
// Particle cap: 600 total active across all bursts. 601st particle is silently dropped.
//
// RNG: LCG (same parameters as confetti-pool.ts). Optional seed injection for tests.

import * as THREE from 'three';
import type {
  EnvCeremonyPool,
  EnvCeremonyPoolDeps,
  EnvCeremonySpawnArgs,
  EnvCeremonyPoolId,
} from './env-ceremony-types';
import type { CeremonyEvent } from './ceremony-events';
import { CONFETTI_COLORS } from './confetti-pool';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Deterministic burst trigger times (seconds from acquire). */
const BURST_TIMES = [0.0, 0.8, 1.5, 2.2, 3.0, 3.8, 4.5, 5.2] as const;

/** Time of the last burst (seconds). */
const SCHEDULE_END = 5.2;

/** Particle TTL (seconds) — opacity fades 1→0 over this duration. */
const PARTICLE_TTL = 1.8;

/** Total emitter lifetime gate. Must be > SCHEDULE_END + PARTICLE_TTL. */
const DONE_THRESHOLD = SCHEDULE_END + PARTICLE_TTL; // 7.0s

/** Sky position ranges for burst origin (X, Y, Z). */
const SKY_X_RANGE = 30; // X ∈ [-15, +15]
const SKY_Y_MIN = 12;
const SKY_Y_RANGE = 6; // Y ∈ [12, 18]
const SKY_Z_RANGE = 30; // Z ∈ [-15, +15]

/** Particles per burst: between MIN and MAX (sampled by LCG). */
const BURST_MIN_PARTICLES = 60;
const BURST_MAX_PARTICLES = 30; // BURST_MIN + BURST_MAX = range max (90 − 60 = 30)

/** Particle outward speed range (units/sec). */
const SPEED_MIN = 5;
const SPEED_RANGE = 3; // [5, 8]

/** Gravity applied per-frame to vy (units/sec²). */
const GRAVITY = -3.5;

/** Total active particle cap. 601st would-be particle is silently dropped. */
const PARTICLE_CAP = 600;

/** Sphere geometry resolution (low-poly for performance). */
const SPHERE_RADIUS = 0.1;
const SPHERE_W_SEGS = 4;
const SPHERE_H_SEGS = 3;

// ─────────────────────────────────────────────────────────────────────────────
// LCG RNG (deterministic) — same parameters as confetti-pool.ts
// ─────────────────────────────────────────────────────────────────────────────

function makeLcg(seed: number): () => number {
  let state = seed;
  return function next(): number {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Config — extends pool deps with optional seed injection
// ─────────────────────────────────────────────────────────────────────────────

export interface FireworksPoolConfig extends EnvCeremonyPoolDeps {
  readonly seed?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-particle state (internal)
// ─────────────────────────────────────────────────────────────────────────────

interface ParticleState {
  mesh: THREE.Mesh;
  geometry: THREE.SphereGeometry;
  material: THREE.MeshBasicMaterial;
  vx: number;
  vy: number;
  vz: number;
  age: number; // seconds since spawn
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-emitter state (internal)
// ─────────────────────────────────────────────────────────────────────────────

interface FireworksEmitterState {
  group: THREE.Group;
  /** Active particles keyed by their mesh for O(1) lookup. */
  activeParticles: Map<THREE.Object3D, ParticleState>;
  elapsed: number;
  finished: boolean;
  /** Index of the next burst to fire (0–7). */
  nextBurstIndex: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createFireworksPool({
  scene,
  registry: _registry, // registry not used for particle physics — update-loop driven
  seed = 8675309,
}: FireworksPoolConfig): EnvCeremonyPool {
  // Single-instance state
  let currentEmitter: THREE.Object3D | null = null;
  let currentState: FireworksEmitterState | null = null;
  let disposed = false;

  // Shared RNG — advances per particle spawn; reset on new acquire for determinism.
  let rng: () => number = makeLcg(seed);

  // ─────────────────────────────────────────────────────────────────────────
  // Internal helpers
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Dispose a single particle: remove from group, dispose geometry + material.
   * Returns the number of particles removed from the active set (always 1).
   */
  function disposeParticle(state: FireworksEmitterState, pState: ParticleState): void {
    state.group.remove(pState.mesh);
    pState.geometry.dispose();
    pState.material.dispose();
    state.activeParticles.delete(pState.mesh);
  }

  /**
   * Spawn a single burst of 60–90 particles at a deterministic sky position.
   * `initialAge` accounts for the time elapsed since the burst trigger time
   * within the current update step (prevents all particles dying instantly
   * when update() is called with a large dt that spans multiple burst triggers).
   * Silently drops particles that would exceed the PARTICLE_CAP.
   */
  function spawnBurst(state: FireworksEmitterState, initialAge: number): void {
    // Burst origin (deterministic LCG)
    const bx = (rng() - 0.5) * SKY_X_RANGE;
    const by = SKY_Y_MIN + rng() * SKY_Y_RANGE;
    const bz = (rng() - 0.5) * SKY_Z_RANGE;

    // Particle count for this burst: [BURST_MIN, BURST_MIN + BURST_MAX]
    const count = BURST_MIN_PARTICLES + Math.floor(rng() * (BURST_MAX_PARTICLES + 1));

    for (let i = 0; i < count; i++) {
      // Cap check: silently drop if at maximum
      if (state.activeParticles.size >= PARTICLE_CAP) break;

      // Particle's age accounts for residual time since burst fired
      const particleAge = Math.max(0, initialAge);

      // Already dead before it even spawned (burst fired too long ago in this step)
      if (particleAge >= PARTICLE_TTL) {
        // Still consume RNG calls to keep determinism for subsequent particles
        rng(); // color
        rng();
        rng();
        rng();
        rng(); // direction (theta, phi) and speed
        continue;
      }

      // Random colour from CONFETTI_COLORS (deterministic LCG index)
      const colorHex = CONFETTI_COLORS[Math.floor(rng() * CONFETTI_COLORS.length)];

      // Random outward direction via spherical LCG sampling
      // theta ∈ [0, 2π], phi ∈ [0, π]
      const theta = rng() * Math.PI * 2;
      const phi = rng() * Math.PI;
      const sinPhi = Math.sin(phi);
      const dx = sinPhi * Math.cos(theta);
      const dy = Math.cos(phi);
      const dz = sinPhi * Math.sin(theta);

      // Speed ∈ [SPEED_MIN, SPEED_MIN + SPEED_RANGE]
      const speed = SPEED_MIN + rng() * SPEED_RANGE;

      // Per-particle geometry + material (each owns its own for disposal)
      const geometry = new THREE.SphereGeometry(SPHERE_RADIUS, SPHERE_W_SEGS, SPHERE_H_SEGS);
      const material = new THREE.MeshBasicMaterial({
        color: colorHex,
        transparent: true,
        opacity: 1.0 - particleAge / PARTICLE_TTL,
      });

      // Apply residual physics for initial age (gravity + velocity integration)
      const vx0 = dx * speed;
      const vy0 = dy * speed;
      const vz0 = dz * speed;
      const t = particleAge;
      // Gravity integration: vy(t) = vy0 + GRAVITY*t, y(t) = vy0*t + 0.5*GRAVITY*t²
      const px = bx + vx0 * t;
      const py = by + vy0 * t + 0.5 * GRAVITY * t * t;
      const pz = bz + vz0 * t;
      // Current vy after gravity integration
      const vyNow = vy0 + GRAVITY * t;

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(px, py, pz);

      const pState: ParticleState = {
        mesh,
        geometry,
        material,
        vx: vx0,
        vy: vyNow,
        vz: vz0,
        age: particleAge,
      };

      state.activeParticles.set(mesh, pState);
      state.group.add(mesh);
    }
  }

  /**
   * Release all particles in the emitter: dispose each particle's geometry +
   * material, clear the active set, and remove the group from scene.
   */
  function releaseState(state: FireworksEmitterState): void {
    // Dispose all live particles
    const particles = Array.from(state.activeParticles.values());
    for (const pState of particles) {
      disposeParticle(state, pState);
    }
    state.activeParticles.clear();

    scene.remove(state.group);
    state.finished = true;
  }

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(_args: EnvCeremonySpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // F6 re-entry guard: release existing emitter before creating a new one
    if (currentEmitter !== null && currentState !== null) {
      releaseState(currentState);
      currentEmitter = null;
      currentState = null;
    }

    // Reset RNG for deterministic spawns (seed-based)
    rng = makeLcg(seed);

    const group = new THREE.Group();
    scene.add(group);

    const state: FireworksEmitterState = {
      group,
      activeParticles: new Map(),
      elapsed: 0,
      finished: false,
      nextBurstIndex: 0,
    };

    currentEmitter = group;
    currentState = state;

    return group;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    if (disposed) return new Set();
    if (!currentState || !currentEmitter) return new Set();

    const state = currentState;
    const prevElapsed = state.elapsed;
    state.elapsed += deltaSeconds;

    // ── 1. Advance physics for particles already alive (born in previous ticks) ─
    //
    // IMPORTANT: physics runs BEFORE new bursts spawn so that newly-spawned
    // particles do NOT get aged/moved in the same tick they are born. This lets
    // callers like `update(2.0)` produce surviving particles from bursts that
    // fire within the [prevElapsed, elapsed] window (e.g. burst at t=1.5 when
    // prevElapsed=0, elapsed=2.0 yields initialAge=0.5 which is < TTL=1.8).
    const toRemove: ParticleState[] = [];

    for (const pState of state.activeParticles.values()) {
      pState.age += deltaSeconds;

      if (pState.age >= PARTICLE_TTL) {
        // Particle lifetime exhausted
        toRemove.push(pState);
        continue;
      }

      // Apply gravity to vy
      pState.vy += GRAVITY * deltaSeconds;

      // Advance position
      pState.mesh.position.x += pState.vx * deltaSeconds;
      pState.mesh.position.y += pState.vy * deltaSeconds;
      pState.mesh.position.z += pState.vz * deltaSeconds;

      // Fade opacity: linear 1→0 over TTL
      const opacity = 1.0 - pState.age / PARTICLE_TTL;
      pState.material.opacity = Math.max(0, opacity);
    }

    // ── 2. Dispose expired particles ───────────────────────────────────────
    for (const pState of toRemove) {
      disposeParticle(state, pState);
    }

    // ── 3. Fire any bursts whose trigger time was crossed in this tick ─────
    //
    // initialAge = (state.elapsed − burstTime): how much simulated time has
    // elapsed since the burst fired within this update step.
    // - Bursts with initialAge ≥ PARTICLE_TTL spawn 0 visible particles (all
    //   dead-on-arrival; RNG still consumed for determinism).
    // - Bursts with 0 ≤ initialAge < PARTICLE_TTL spawn particles pre-integrated
    //   to their current position/age so they appear correctly this frame.
    while (
      state.nextBurstIndex < BURST_TIMES.length &&
      BURST_TIMES[state.nextBurstIndex] <= state.elapsed
    ) {
      const burstTime = BURST_TIMES[state.nextBurstIndex];
      // Only fire bursts that haven't already been fired (burstTime ≥ prevElapsed).
      // (If prevElapsed=0 and burstTime=0.0, `>= 0` is true → fires correctly.)
      if (burstTime >= prevElapsed) {
        const initialAge = state.elapsed - burstTime;
        spawnBurst(state, initialAge);
      }
      state.nextBurstIndex++;
    }

    // ── 4. Check completion ────────────────────────────────────────────────
    if (state.elapsed >= DONE_THRESHOLD && state.activeParticles.size === 0) {
      state.finished = true;
    }

    return new Set([currentEmitter]);
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    if (emitter !== currentEmitter) {
      // Stale ref or unknown object — treat as finished
      return true;
    }
    return currentState ? currentState.finished : true;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    if (emitter !== currentEmitter) return; // safe no-op for stale/unknown refs
    if (currentState) {
      releaseState(currentState);
    }
    currentEmitter = null;
    currentState = null;
  }

  // ── dispose (F4) ───────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    if (currentState) {
      releaseState(currentState);
      currentEmitter = null;
      currentState = null;
    }
  }

  return { mode: 'ceremony', acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a CeremonyEvent to fireworks spawn args.
 * Returns null for any event other than game_over.
 */
export function resolveFireworksForEvent(
  event: CeremonyEvent,
): { poolId: EnvCeremonyPoolId; args: EnvCeremonySpawnArgs } | null {
  if (event.type !== 'game_over') return null;

  return {
    poolId: 'fireworks',
    args: {},
  };
}
